import FormErrors from 'app/utils/form_errors'
import get from 'lodash/get'
import { default as deserialize, formatMeta } from 'app/utils/deserialize'
import { fetch, create, remove, update } from 'app/utils/data'
import { buildFilters } from './utils'

const SECTIONS = ['invoices', 'kits', 'tokens', 'licenses', 'meteredBillings', 'meteredBillingInvoices', 'teamMembers', 'usage']

function emptySectionState() {
  return SECTIONS.reduce((acc, section) => {
    acc[section] = section === 'usage' ? null : []
    return acc
  }, {})
}

function emptySectionLoading() {
  return SECTIONS.reduce((acc, section) => {
    acc[section] = false
    return acc
  }, {})
}

function emptySectionErrors() {
  return SECTIONS.reduce((acc, section) => {
    acc[section] = null
    return acc
  }, {})
}

export default {
  namespaced: true,

  state: {
    list: [],
    item: null,
    pagination: {},
    sections: emptySectionState(),
    // sectionsLoading / sectionsErrors are populated by fetchSection but not
    // yet read by DetailView — kept here as plumbing for the follow-up that
    // adds per-section spinners and error UI.
    sectionsLoading: emptySectionLoading(),
    sectionsErrors: emptySectionErrors()
  },

  getters: {
    currentAccounts: (state) => state.list,
    currentAccount: (state) => state.item,
    currentPagination: (state) => state.pagination,
    currentAccountSections: (state) => state.sections,
    currentAccountSectionsLoading: (state) => state.sectionsLoading,
    currentAccountSectionsErrors: (state) => state.sectionsErrors
  },

  mutations: {
    accountsFetched(state, { accounts }) {
      state.list = accounts
    },

    accountFetched(state, { account }) {
      state.item = account
    },

    paginationFetched(state, { pagination }) {
      state.pagination = pagination
    },

    sectionFetched(state, { section, data }) {
      state.sections = { ...state.sections, [section]: data }
    },

    sectionLoading(state, { section, loading }) {
      state.sectionsLoading = { ...state.sectionsLoading, [section]: loading }
    },

    sectionError(state, { section, error }) {
      state.sectionsErrors = { ...state.sectionsErrors, [section]: error }
    },

    resetSections(state) {
      state.sections = emptySectionState()
      state.sectionsLoading = emptySectionLoading()
      state.sectionsErrors = emptySectionErrors()
    }
  },

  actions: {
    async fetchAccounts({ commit }, { page = 1, filters = {} }) {
      const response = await fetch({
        url: '/api/admin/accounts',
        params: {
          page,
          ...buildFilters(filters)
        }
      })

      const accounts = await deserialize(response.data)
      const pagination = formatMeta(get(response.data, 'meta', {}))

      commit('accountsFetched', { accounts })
      commit('paginationFetched', { pagination })
    },

    async fetchAccount({ commit }, accountId) {
      const response = await fetch({ url: `/api/admin/accounts/${accountId}` })
      const account = await deserialize(response.data)

      commit('accountFetched', { account })
    },

    async fetchAccountInvoices({ dispatch }, accountId) {
      return dispatch('fetchSection', { section: 'invoices', path: 'invoices', accountId })
    },

    async fetchAccountKits({ dispatch }, accountId) {
      return dispatch('fetchSection', { section: 'kits', path: 'kits', accountId })
    },

    async fetchAccountTokens({ dispatch }, accountId) {
      return dispatch('fetchSection', { section: 'tokens', path: 'tokens', accountId })
    },

    async fetchAccountLicenses({ dispatch }, accountId) {
      return dispatch('fetchSection', { section: 'licenses', path: 'licenses', accountId })
    },

    async fetchAccountMeteredBillings({ dispatch }, accountId) {
      return dispatch('fetchSection', { section: 'meteredBillings', path: 'metered-billings', accountId })
    },

    async fetchAccountTeamMembers({ dispatch }, accountId) {
      return dispatch('fetchSection', { section: 'teamMembers', path: 'team-members', accountId })
    },

    async fetchAccountUsage({ dispatch }, accountId) {
      return dispatch('fetchSection', { section: 'usage', path: 'usage', accountId })
    },

    async fetchSection({ commit }, { section, path, accountId }) {
      commit('sectionLoading', { section, loading: true })
      commit('sectionError', { section, error: null })
      try {
        const response = await fetch({ url: `/api/admin/accounts/${accountId}/${path}` })
        const data = await deserialize(response.data)
        commit('sectionFetched', { section, data })
      } catch (error) {
        commit('sectionError', { section, error })
        console.error(`Failed to load admin account section "${section}":`, error)
      } finally {
        commit('sectionLoading', { section, loading: false })
      }
    },

    async fetchAccountByEmail(_context, email) {
      const response = await fetch({ url: `/api/admin/accounts/lookup/${encodeURIComponent(email)}` })

      if (response.data && response.data.data !== null) {
        return await deserialize(response.data)
      } else {
        return null
      }
    },

    async deleteAccount(_context, accountId) {
      await remove({ url: `/api/admin/accounts/${accountId}` })
    },

    async deleteInvoice({ dispatch }, { accountId, invoiceId }) {
      await remove({ url: `/api/admin/accounts/${accountId}/invoices/${invoiceId}` })

      dispatch('fetchAccountInvoices', accountId)
    },

    async updateExternalToken({ dispatch }, { accountId, active }) {
      const changes = { active: active }

      await update({
        url: `/api/admin/tools/token/${accountId}`,
        patch: true,
        changes
      })

      dispatch('fetchAccountTokens', accountId)
    },

    async passwordReset({ dispatch }, data) {
      const response = await create({
        url: '/api/admin/tools/passwords',
        data: data
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async confirmAccount({ dispatch }, changes) {
      const response = await create({
        url: '/api/admin/tools/confirmations',
        data: changes
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async resendConfirmation({ dispatch }, changes) {
      const response = await create({
        url: '/api/admin/tools/confirmations/resend',
        data: changes
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async resendTeamInvitation({ dispatch }, { accountId, teamMemberEmail }) {
      try {
        await create({
          url: '/api/admin/tools/team-invite',
          data: {
            email: teamMemberEmail
          }
        })

        dispatch('fetchAccountTeamMembers', accountId)
      } catch (error) {
        throw new FormErrors(error.response.data.error)
      }
    },

    async transferLicense({ dispatch }, changes) {
      const response = await create({
        url: '/api/admin/tools/transfer',
        data: changes
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
      dispatch('fetchAccountLicenses', account.id)
    },

    async previewKitTransfer(_context, { kitIds, email }) {
      const response = await create({
        url: '/api/admin/tools/transfer-kits/preview',
        type: 'kit-transfer',
        data: { kit_ids: kitIds, email }
      })

      return response.data
    },

    async transferKits({ dispatch }, { accountId, kitIds, email }) {
      const response = await create({
        url: '/api/admin/tools/transfer-kits',
        type: 'kit-transfer',
        data: { kit_ids: kitIds, email }
      })

      dispatch('fetchAccountKits', accountId)

      return response.data
    },

    async cancelSubscription({ dispatch }, changes) {
      const response = await create({
        url: '/api/admin/tools/subscriptions',
        data: changes
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async createTeamOwner({ dispatch }, { accountId }) {
      await create({
        url: '/api/admin/tools/team-owner',
        data: { accountId }
      })

      dispatch('fetchAccountTeamMembers', accountId)
    },

    async reconcileSubscription({ dispatch }, changes) {
      const response = await create({
        url: '/api/admin/tools/subscriptions/reconcile',
        data: changes
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async updateAccount({ state, dispatch }, changes) {
      const response = await update({
        url: `/api/admin/accounts/${state.item.id}`,
        patch: true,
        type: 'account',
        changes,
        record: state.item
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async activateServices({ dispatch }, { accountId }) {
      await update({ url: `/api/admin/accounts/${accountId}/services` })

      dispatch('fetchAccount', accountId)
    },

    async deactivateServices({ dispatch }, { accountId }) {
      await remove({ url: `/api/admin/accounts/${accountId}/services` })

      dispatch('fetchAccount', accountId)
    },

    async changePaymentMethod({ dispatch }, { accountId }) {
      await update({ url: `/api/admin/accounts/${accountId}/payment-method` })

      dispatch('fetchAccount', accountId)
    },

    async createMissingInvoice({ dispatch }, { accountId }) {
      await create({ url: `/api/admin/accounts/${accountId}/invoices`, data: {} })

      dispatch('fetchAccountInvoices', accountId)
    },

    async activateKits({ dispatch }, { accountId }) {
      await update({ url: `/api/admin/accounts/${accountId}/kits` })

      dispatch('fetchAccountKits', accountId)
    },

    async deactivateKits({ dispatch }, { accountId }) {
      await remove({ url: `/api/admin/accounts/${accountId}/kits` })

      dispatch('fetchAccountKits', accountId)
    },

    async toggleKitsShutdown({ dispatch, state }, { accountId, toggle }) {
      const changes = { neverShutdown: toggle }

      await update({
        url: `/api/admin/accounts/${accountId}/kits`,
        patch: true,
        type: 'account',
        changes,
        record: state.item
      })

      dispatch('fetchAccountKits', accountId)
    },

    async toggleMeteredBandwidth({ dispatch, state }, { id, accountId, toggle }) {
      const changes = { meteredBandwidthOn: toggle }

      await update({
        url: `/api/admin/metered-billing/${id}`,
        patch: true,
        changes,
        record: state.item
      })

      dispatch('fetchAccountMeteredBillings', accountId)
    },

    async toggleMeteredPageviews({ dispatch, state }, { id, accountId, toggle }) {
      const changes = { meteredPageviewsOn: toggle }

      await update({
        url: `/api/admin/metered-billing/${id}`,
        patch: true,
        changes,
        record: state.item
      })

      dispatch('fetchAccountMeteredBillings', accountId)
    }
  }
}
